// =============================================================================
// Canonical property rooms (single source of truth per property).
// Checklist and Inventory both reference property_rooms.
// =============================================================================

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { queryKeys } from "@/lib/queryKeys";
import { matchRoom, normalizeRoomName, type MatchRoomResult } from "@/lib/roomUtils";
import { replaceChecklistRoomLinks } from "@/lib/propertyChecklistRooms";

export interface PropertyRoom {
  id: string;
  property_id: string;
  name: string;
  name_normalized: string;
  room_instructions: string | null;
  notes: string | null;
  archived_at: string | null;
  created_at: string;
}

export function usePropertyRooms(propertyId: string | undefined, options?: { includeArchived?: boolean }) {
  const includeArchived = options?.includeArchived ?? false;
  return useQuery({
    queryKey: [...queryKeys.propertyRooms.byProperty(propertyId ?? ""), includeArchived],
    queryFn: async (): Promise<PropertyRoom[]> => {
      if (!propertyId) return [];
      let q = supabase
        .from("property_rooms")
        .select("id, property_id, name, name_normalized, room_instructions, notes, archived_at, created_at")
        .eq("property_id", propertyId)
        .order("name_normalized", { ascending: true });
      if (!includeArchived) {
        q = q.is("archived_at", null);
      }
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as PropertyRoom[];
    },
    enabled: !!propertyId,
  });
}

export type RoomUsage = { checklists: number; inventory: number; issues: number };

/** Per-room usage counts (checklists, inventory, open issues) for display. */
export function useRoomUsageMap(propertyId: string | undefined, rooms: PropertyRoom[]) {
  const roomIds = rooms.map((r) => r.id);
  return useQuery({
    queryKey: ["room-usage", propertyId, roomIds.join(",")],
    queryFn: async (): Promise<Record<string, RoomUsage>> => {
      if (!propertyId || roomIds.length === 0) return {};
      const [linksRes, invRes, issuesRes] = await Promise.all([
        supabase
          .from("checklist_room_links")
          .select("property_room_id")
          .in("property_room_id", roomIds),
        supabase
          .from("inventory_rooms")
          .select("property_room_id")
          .eq("property_id", propertyId)
          .in("property_room_id", roomIds),
        supabase
          .from("maintenance_issues")
          .select("room_name")
          .eq("property_id", propertyId)
          .in("status", ["open", "in_progress"]),
      ]);
      if (linksRes.error) throw linksRes.error;
      if (invRes.error) throw invRes.error;
      if (issuesRes.error) throw issuesRes.error;
      const count = (arr: { property_room_id?: string; room_name?: string }[], key: "property_room_id" | "room_name", value: string) =>
        arr.filter((r) => r[key] === value).length;
      const linkList = linksRes.data ?? [];
      const invList = invRes.data ?? [];
      const issuesList = issuesRes.data ?? [];
      const issuesByRoomName: Record<string, number> = {};
      for (const r of issuesList as { room_name: string }[]) {
        const n = r.room_name?.trim() ?? "";
        issuesByRoomName[n] = (issuesByRoomName[n] ?? 0) + 1;
      }
      const map: Record<string, RoomUsage> = {};
      for (const room of rooms) {
        map[room.id] = {
          checklists: count(linkList as { property_room_id: string }[], "property_room_id", room.id),
          inventory: count(invList as { property_room_id: string }[], "property_room_id", room.id),
          issues: issuesByRoomName[room.name.trim()] ?? 0,
        };
      }
      return map;
    },
    enabled: !!propertyId && roomIds.length > 0,
  });
}

/** Create a canonical room; returns the new row. Restores an archived room of the same name. */
export function useCreatePropertyRoom() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      propertyId,
      name,
      room_instructions,
      notes,
    }: {
      propertyId: string;
      name: string;
      room_instructions?: string | null;
      notes?: string | null;
    }): Promise<PropertyRoom> => {
      const trimmed = name.trim();
      if (!trimmed) throw new Error("Room name is required");
      const name_normalized = normalizeRoomName(trimmed);
      if (!name_normalized) throw new Error("Room name is required");

      const { data: existing, error: lookupError } = await supabase
        .from("property_rooms")
        .select("id, property_id, name, name_normalized, room_instructions, notes, archived_at, created_at")
        .eq("property_id", propertyId)
        .eq("name_normalized", name_normalized)
        .maybeSingle();
      if (lookupError) throw lookupError;

      if (existing) {
        if (!existing.archived_at) {
          throw new Error("A room with this name already exists");
        }
        const { data: restored, error: restoreError } = await supabase
          .from("property_rooms")
          .update({
            archived_at: null,
            name: trimmed,
            room_instructions: room_instructions?.trim() || existing.room_instructions,
            notes: notes?.trim() || existing.notes,
          })
          .eq("id", existing.id)
          .eq("property_id", propertyId)
          .select()
          .single();
        if (restoreError) throw restoreError;
        return restored as PropertyRoom;
      }

      const { data, error } = await supabase
        .from("property_rooms")
        .insert({
          property_id: propertyId,
          name: trimmed,
          name_normalized,
          room_instructions: room_instructions?.trim() || null,
          notes: notes?.trim() || null,
        })
        .select()
        .single();
      if (error) {
        if (error.code === "23505") throw new Error("A room with this name already exists");
        throw error;
      }
      return data as PropertyRoom;
    },
    onSuccess: (row, variables) => {
      queryClient.setQueryData<PropertyRoom[]>(
        [...queryKeys.propertyRooms.byProperty(variables.propertyId), false],
        (old) => {
          const list = old ?? [];
          if (list.some((r) => r.id === row.id)) {
            return list.map((r) => (r.id === row.id ? row : r));
          }
          return [...list, row].sort((a, b) => a.name_normalized.localeCompare(b.name_normalized));
        },
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.propertyRooms.byProperty(variables.propertyId) });
      queryClient.invalidateQueries({ queryKey: ["checklists", variables.propertyId] });
      queryClient.invalidateQueries({ queryKey: ["inventory-rooms", variables.propertyId] });
    },
  });
}

/** Update a property room (name, room_instructions, notes). */
export function useUpdatePropertyRoom() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      propertyId,
      roomId,
      name,
      room_instructions,
      notes,
    }: {
      propertyId: string;
      roomId: string;
      name: string;
      room_instructions?: string | null;
      notes?: string | null;
    }): Promise<PropertyRoom> => {
      const trimmed = name.trim();
      if (!trimmed) throw new Error("Room name is required");
      const name_normalized = normalizeRoomName(trimmed);
      const { data: current, error: readErr } = await supabase
        .from("property_rooms")
        .select("name")
        .eq("id", roomId)
        .eq("property_id", propertyId)
        .maybeSingle();
      if (readErr) throw readErr;
      const previous = current?.name ?? "";
      const patch: { name: string; name_normalized: string; room_instructions?: string | null; notes?: string | null } = {
        name: trimmed,
        name_normalized,
      };
      if (room_instructions !== undefined) patch.room_instructions = room_instructions?.trim() || null;
      if (notes !== undefined) patch.notes = notes?.trim() || null;
      const { data, error } = await supabase
        .from("property_rooms")
        .update(patch)
        .eq("id", roomId)
        .eq("property_id", propertyId)
        .select()
        .single();
      if (error) {
        if (error.code === "23505") throw new Error("A room with this name already exists");
        throw error;
      }
      if (previous && previous !== trimmed) {
        const { error: invErr } = await supabase
          .from("inventory_rooms")
          .update({ name: trimmed })
          .eq("property_id", propertyId)
          .eq("property_room_id", roomId);
        if (invErr) throw invErr;
        const { error: unitErr } = await supabase
          .from("property_unit_items")
          .update({ room: trimmed })
          .eq("property_id", propertyId)
          .eq("room", previous);
        if (unitErr) throw unitErr;
        const { error: appErr } = await supabase
          .from("appliances")
          .update({ location: trimmed })
          .eq("property_id", propertyId)
          .eq("location", previous);
        if (appErr) throw appErr;
      }
      return data as PropertyRoom;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.propertyRooms.byProperty(variables.propertyId) });
      queryClient.invalidateQueries({ queryKey: ["checklists", variables.propertyId] });
      queryClient.invalidateQueries({ queryKey: ["inventory-rooms", variables.propertyId] });
      queryClient.invalidateQueries({ queryKey: queryKeys.unitItems.byProperty(variables.propertyId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.maintenance.appliances(variables.propertyId) });
    },
  });
}

/** Archive a property room (hide from pickers; only Rooms tab can unarchive). */
export function useArchivePropertyRoom() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ propertyId, roomId }: { propertyId: string; roomId: string }) => {
      const { data, error } = await supabase
        .from("property_rooms")
        .update({ archived_at: new Date().toISOString() })
        .eq("id", roomId)
        .eq("property_id", propertyId)
        .select()
        .single();
      if (error) throw error;
      return data as PropertyRoom;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.propertyRooms.byProperty(variables.propertyId) });
      queryClient.invalidateQueries({ queryKey: ["checklists", variables.propertyId] });
      queryClient.invalidateQueries({ queryKey: ["inventory-rooms", variables.propertyId] });
    },
  });
}

/** Unarchive a property room. */
export function useUnarchivePropertyRoom() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ propertyId, roomId }: { propertyId: string; roomId: string }) => {
      const { data, error } = await supabase
        .from("property_rooms")
        .update({ archived_at: null })
        .eq("id", roomId)
        .eq("property_id", propertyId)
        .select()
        .single();
      if (error) throw error;
      return data as PropertyRoom;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.propertyRooms.byProperty(variables.propertyId) });
      queryClient.invalidateQueries({ queryKey: ["checklists", variables.propertyId] });
      queryClient.invalidateQueries({ queryKey: ["inventory-rooms", variables.propertyId] });
    },
  });
}

/** Delete a property room. Caller should check usage (checklist_room_links, inventory_rooms) first. */
export function useDeletePropertyRoom() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ propertyId, roomId }: { propertyId: string; roomId: string }) => {
      const { error } = await supabase
        .from("property_rooms")
        .delete()
        .eq("id", roomId)
        .eq("property_id", propertyId);
      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.propertyRooms.byProperty(variables.propertyId) });
      queryClient.invalidateQueries({ queryKey: ["checklists", variables.propertyId] });
      queryClient.invalidateQueries({ queryKey: ["inventory-rooms", variables.propertyId] });
    },
  });
}

/**
 * Resolve input name to a property_room: exact match, or suggest "Did you mean X?", or create new.
 * Caller can use result.exact, result.suggested (prompt user), or create new with result.nameNormalized.
 */
export function matchRoomAgainstProperty(
  propertyId: string | undefined,
  existingRooms: PropertyRoom[],
  inputName: string
): MatchRoomResult {
  return matchRoom(inputName, existingRooms.map((r) => ({ id: r.id, name: r.name })));
}

/** Checklist room links: which property_rooms are linked to this checklist */
export function useChecklistRoomLinks(checklistId: string | undefined) {
  return useQuery({
    queryKey: ["checklist-room-links", checklistId],
    queryFn: async () => {
      if (!checklistId) return [];
      const { data, error } = await supabase
        .from("checklist_room_links")
        .select("id, checklist_id, property_room_id, room_name_original")
        .eq("checklist_id", checklistId);
      if (error) throw error;
      return (data ?? []) as { id: string; checklist_id: string; property_room_id: string; room_name_original: string | null }[];
    },
    enabled: !!checklistId,
  });
}

/** Upsert checklist_room_links for a checklist (replace existing links for that checklist). */
export function useSetChecklistRoomLinks() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      checklistId,
      propertyId,
      propertyRoomIds,
      roomNamesOriginal,
    }: {
      checklistId: string;
      propertyId: string;
      propertyRoomIds: string[];
      roomNamesOriginal?: (string | null)[];
    }) => {
      await replaceChecklistRoomLinks(checklistId, propertyRoomIds, roomNamesOriginal);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["checklist-room-links", variables.checklistId] });
      queryClient.invalidateQueries({ queryKey: ["checklists", variables.propertyId] });
    },
  });
}

/** Merge source property room into target: update all refs (including maintenance issues by room_name) then delete source. */
export function useMergePropertyRooms() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      propertyId,
      sourceRoomId,
      targetRoomId,
      sourceRoomName,
      targetRoomName,
    }: {
      propertyId: string;
      sourceRoomId: string;
      targetRoomId: string;
      sourceRoomName?: string;
      targetRoomName?: string;
    }) => {
      if (sourceRoomId === targetRoomId) throw new Error("Source and target must differ");
      await supabase.from("inventory_rooms").update({ property_room_id: targetRoomId }).eq("property_room_id", sourceRoomId);
      await supabase.from("checklist_room_links").update({ property_room_id: targetRoomId }).eq("property_room_id", sourceRoomId);
      if (sourceRoomName != null && targetRoomName != null && sourceRoomName !== targetRoomName) {
        await supabase
          .from("maintenance_issues")
          .update({ room_name: targetRoomName })
          .eq("property_id", propertyId)
          .eq("room_name", sourceRoomName);
      }
      const { error } = await supabase.from("property_rooms").delete().eq("id", sourceRoomId);
      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.propertyRooms.byProperty(variables.propertyId) });
      queryClient.invalidateQueries({ queryKey: ["inventory-rooms", variables.propertyId] });
      queryClient.invalidateQueries({ queryKey: ["checklists", variables.propertyId] });
      queryClient.invalidateQueries({ queryKey: queryKeys.maintenance.byProperty(variables.propertyId) });
    },
  });
}
